"use client";
import React, {
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import "swiper/css";
// navigation.css فقط دکمه‌های پیش‌فرض Swiper را استایل می‌کند و این‌جا دکمه‌ها سفارشی‌اند؛ تنها قانون لازمش
// (`.swiper-button-lock`) در global.css است. thumbs.css خالی است.
import "swiper/css/pagination";
import "swiper/css/free-mode";
import "swiper/css/zoom";
import "swiper/css/grid";

import { Swiper, SwiperSlide } from "swiper/react";
import {
	limit_slide_priority,
	visible_slide_count,
} from "@core/helpers/client/contexts/ImagePriorityContext";
import { ImageSizesProvider, slider_image_sizes } from "@core/helpers/client/contexts/ImageSizesContext";
import {
	Navigation,
	Pagination,
	FreeMode,
	Thumbs,
	Autoplay,
	Zoom,
	EffectCoverflow,
	Parallax,
	EffectFade,
	Keyboard,
	Grid
} from "swiper/modules";

import { useLanguageContext } from "@core/helpers/client/contexts/LanguagesContext";
import { ChevronLeft, ChevronRight } from "lucide-react";
import StaticSlider from "@core/helpers/client/components/static-slider";

const THUMB_MODULES = [Navigation, FreeMode, Thumbs];

const KEYBOARD_OPTIONS = { enabled: true, onlyInViewport: true };

// اندازه‌ی دکمه‌های ناوبری کنار اسلایدر (هم‌خوان با `.nav-sides` در global.css)
const NAV_BUTTON_SIZE = 40;

const RENDER_FRACTION = (currentClass, totalClass) =>
	`<div class="fraction-pagination">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="lucide lucide-image"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                            <span class="${currentClass}"></span>/<span class="${totalClass}"></span>
                        </div>`;

const RENDER_BULLET = (index, className) => `<span class="${className}"></span>`;

const useIsomorphicLayoutEffect =
	typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * آپدیت ایمن اسلایدر.
 *
 * دو حالت باعث خطای «Cannot read properties of undefined (reading
 * 'getAttribute')» می‌شد:
 *
 * ۱) نمونه‌ی سوایپر پیش از اجرای فریم بعدی از بین رفته باشد (آن‌مانت، تغییر
 *    مسیر، یا ساخته شدن دوباره‌ی Swiper).
 * ۲) اسلایدرهای گرید (`grid.rows > 1`): سوایپر در `updateActiveIndex` ستون
 *    اسلاید فعال را با `slides.find(s => s.column === activeIndex)` پیدا
 *    می‌کند و اگر ستون‌های گرید هنوز ساخته نشده باشند، نتیجه undefined است و
 *    درجا `getAttribute` روی آن صدا زده می‌شود.
 *
 * حالت اول با شرط و حالت دوم با try/catch پوشش داده شده؛ آپدیت بعدی خود
 * سوایپر (رسایز/اسلاید) وضعیت را درست می‌کند.
 *
 * @returns {boolean} آیا اسلایدر همچنان قابل استفاده است
 */
const safeUpdate = (swiper) => {
	if (!swiper || swiper.destroyed || !swiper.el) return false;

	try {
		swiper.update();
	} catch {
		return false;
	}

	return true;
};

// مقایسه‌ی مقداری آبجکت‌های کوچکِ پیکربندی (breakpoints / grid). نسبت به
// `JSON.stringify` نه رشته‌ای می‌سازد و نه آبجکت واسط، و در حالت رایج (رفرنس
// یکسان، یا اولین کلیدِ متفاوت) خیلی زودتر تمام می‌شود.
const isEqualValue = (a, b) => {
	if (Object.is(a, b)) return true;
	if (typeof a !== "object" || typeof b !== "object" || a === null || b === null)
		return false;

	const a_keys = Object.keys(a);
	if (a_keys.length !== Object.keys(b).length) return false;

	return a_keys.every((key) => isEqualValue(a[key], b[key]));
};

// آبجکت‌های داده‌ای (breakpoints / grid) در محل استفاده inline ساخته می‌شوند؛
// با مقایسه‌ی مقداری، رفرنس پایدار می‌ماند و سوایپر بی‌دلیل آپدیت نمی‌شود.
const useStableValue = (value) => {
	const ref = useRef(value);

	if (!isEqualValue(ref.current, value)) ref.current = value;

	return ref.current;
};

const SwiperComponent = ({
	slides,
	swiper_className,
	swiper_thumbs_className,
	slidesPerView,
	spaceBetween,
	breakpoints,
	navigation = true,
	navPosition = "bottom",
	slides_shadow_space = false,
	hideNavOnMobile = false,
	hidePaginationOnDesktop = false,
	loop,
	pagination,
	paginationType = "bullets",
	thumbnail,
	thumbsSlidesPerView,
	thumbsSpaceBetween,
	thumbnail_custom_array,
	options = {},
	grid,
	grabCursor,
	enableZoom = false,
	autoplay,
	effect,
	parallax_effect,
	speed,
	thumb_breakpoints,
	thumb_draggable,
	centeredSlides,
	thumb_centeredSlides,
	style,
	onSlideChange,
	thumb_navigation,
	className,
	...props
}) => {
	const { direction } = useLanguageContext();

	// تا قبل از mount (html سرور، جاوااسکریپت خاموش) یک ردیف CSS با همان چیدمان کشیده
	// می‌شود تا اسلایدر مثل Swiper دیده شود؛ بعد از mount خود Swiper جایش را می‌گیرد
	const [mounted, set_mounted] = useState(false);
	useEffect(() => {
		set_mounted(true);
	}, []);
	const raw_instance_id = useId();
	const { prev_class, next_class, pagination_class } = useMemo(() => {
		const instance_id = raw_instance_id.replace(/[^a-zA-Z0-9_-]/g, "");

		return {
			prev_class: `custom-swiper-prev-${instance_id}`,
			next_class: `custom-swiper-next-${instance_id}`,
			pagination_class: `custom-swiper-pagination-${instance_id}`
		};
	}, [raw_instance_id]);

	const shadow_space_class = useMemo(
		() =>
			slides_shadow_space
				? `swiper-shadow-space ${/(^|\s)p[xse]?-/.test(swiper_className || "") ? "" : "swiper-shadow-space-x"}`
				: "",
		[slides_shadow_space, swiper_className]
	);

	const mainSwiperRef = useRef(null);
	const prevBtnRef = useRef(null);
	const nextBtnRef = useRef(null);
	const hostRef = useRef(null);

	const [thumbsSwiper, setThumbsSwiper] = useState(null);
	const thumbsSwiperRef = useRef(null);

	const [activeThumbIndex, setActiveThumbIndex] = useState(0);

	const onSlideChangeRef = useRef(onSlideChange);
	onSlideChangeRef.current = onSlideChange;

	const stable_breakpoints = useStableValue(breakpoints);
	const stable_grid = useStableValue(grid);
	const layout = {
		slidesPerView: options?.slidesPerView ?? slidesPerView,
		breakpoints: options?.breakpoints ?? stable_breakpoints,
		grid: options?.grid ?? stable_grid,
	};
	// اسلایدهایی که در نمای اول دیده نمی‌شوند lazy می‌مانند، حتی اگر اسلایدر در یکی از سه ویجت اول باشد
	const visible_slides = visible_slide_count(layout);
	// عرض هر اسلاید از breakpointهای خود اسلایدر؛ عکس کارت‌ها فایلی هم‌اندازه‌ی اسلاید می‌گیرند نه 886px
	const slide_sizes = slider_image_sizes(layout);
	const prioritized_slides = useMemo(
		() => limit_slide_priority(slides, visible_slides),
		[slides, visible_slides]
	);
	const stable_thumb_breakpoints = useStableValue(thumb_breakpoints);

	const active_effect = options?.effect ?? effect;
	const need_navigation = !!navigation;
	const need_pagination = !!pagination;
	const need_grid = !!stable_grid;
	const need_thumbs = !!thumbnail;
	const need_free_mode = !!thumbnail || !!options?.freeMode || !!props.freeMode;
	const need_autoplay = !!autoplay || !!options?.autoplay;
	const need_zoom = !!enableZoom || !!options?.zoom;
	const need_parallax = !!parallax_effect || !!options?.parallax;

	const swiper_modules = useMemo(() => {

		const modules = [Keyboard];

		if (need_navigation) modules.push(Navigation);
		if (need_pagination) modules.push(Pagination);
		if (need_grid) modules.push(Grid);
		if (need_thumbs) modules.push(Thumbs);
		if (need_free_mode) modules.push(FreeMode);
		if (need_autoplay) modules.push(Autoplay);
		if (need_zoom) modules.push(Zoom);
		if (need_parallax) modules.push(Parallax);
		if (active_effect === "coverflow") modules.push(EffectCoverflow);
		if (active_effect === "fade") modules.push(EffectFade);

		return modules;
	}, [
		need_navigation,
		need_pagination,
		need_grid,
		need_thumbs,
		need_free_mode,
		need_autoplay,
		need_zoom,
		need_parallax,
		active_effect
	]);

	const navigation_options = useMemo(
		() =>
			navigation
				? {
					prevEl: `.${prev_class}`,
					nextEl: `.${next_class}`
				}
				: false,
		[navigation, prev_class, next_class]
	);

	const pagination_options = useMemo(
		() =>
			pagination
				? {
					clickable: true,
					el: `.${pagination_class}`,
					type: paginationType === "fraction" ? "fraction" : "bullets",
					renderFraction: RENDER_FRACTION,
					renderBullet: RENDER_BULLET
				}
				: false,
		[pagination, paginationType, pagination_class]
	);

	const thumbs_options = useMemo(
		() => (thumbnail ? { swiper: thumbsSwiper, autoScrollOffset: 0 } : undefined),
		[thumbnail, thumbsSwiper]
	);

	const thumbs_centered = thumb_centeredSlides ?? true;

	const nav_sides = navigation && navPosition !== "bottom";

	const nav_vars_ref = useRef({
		center: null,
		inset_left: null,
		inset_right: null
	});

	const measureNav = useCallback(() => {
		if (!nav_sides) return;

		const swiper = mainSwiperRef.current;
		if (!swiper || swiper.destroyed || !swiper.el || !swiper.wrapperEl) return;

		const el = swiper.el;
		const computed = window.getComputedStyle(el);
		const pad_left = parseFloat(computed.paddingLeft) || 0;
		const pad_right = parseFloat(computed.paddingRight) || 0;

		const rows_top = swiper.wrapperEl.offsetTop || 0;
		const rows_height = swiper.wrapperEl.offsetHeight || 0;

		const btn_width =
			prevBtnRef.current?.offsetWidth ||
			nextBtnRef.current?.offsetWidth ||
			NAV_BUTTON_SIZE;

		const center =
			rows_height > 0 ? Math.round(rows_top + rows_height / 2) : null;
		const inset_right = pad_right >= btn_width ? pad_right - btn_width : pad_right;
		const inset_left = pad_left >= btn_width ? pad_left - btn_width : pad_left;

		const written = nav_vars_ref.current;

		if (center !== null && center !== written.center) {
			written.center = center;
			el.style.setProperty("--swiper-nav-center", `${center}px`);
		}

		if (inset_right !== written.inset_right) {
			written.inset_right = inset_right;
			el.style.setProperty("--swiper-nav-inset-right", `${inset_right}px`);
		}

		if (inset_left !== written.inset_left) {
			written.inset_left = inset_left;
			el.style.setProperty("--swiper-nav-inset-left", `${inset_left}px`);
		}
	}, [nav_sides]);

	const syncNavState = useCallback(
		(swiper) => {
			if (!swiper || swiper.destroyed) return;

			const states = [
				[prevBtnRef.current, !loop && swiper.isBeginning],
				[nextBtnRef.current, !loop && swiper.isEnd]
			];

			states.forEach(([node, is_hidden]) => {
				if (!node) return;

				node.classList.toggle("opacity-100", !is_hidden);
				node.classList.toggle("opacity-0", !!is_hidden);
				node.classList.toggle("pointer-events-none", !!is_hidden);
			});
		},
		[loop]
	);

	const syncThumbs = useCallback((index) => {
		const thumbs = thumbsSwiperRef.current;
		if (!thumbs || thumbs.destroyed) return;
		thumbs.slideTo(index);
	}, []);

	const handleSlideChange = useCallback(
		(swiper) => {
			const index = swiper.realIndex ?? swiper.activeIndex ?? 0;

			if (thumbnail) {
				setActiveThumbIndex(index);
				syncThumbs(index);
			}

			syncNavState(swiper);
			onSlideChangeRef.current?.(index);
		},
		[thumbnail, syncThumbs, syncNavState]
	);

	const handleSwiper = useCallback(
		(swiper) => {
			mainSwiperRef.current = swiper;

			const rafId = requestAnimationFrame(() => {
				if (!safeUpdate(swiper)) return;

				syncNavState(swiper);
				measureNav();

				if (thumbnail) {
					const index = swiper.realIndex ?? swiper.activeIndex ?? 0;
					setActiveThumbIndex(index);
					syncThumbs(index);
				}
			});

			swiper.__initialUpdateRafId = rafId;
		},
		[syncNavState, measureNav, syncThumbs, thumbnail]
	);

	const handleDestroy = useCallback((swiper) => {
		if (swiper?.__initialUpdateRafId) {
			cancelAnimationFrame(swiper.__initialUpdateRafId);
		}
	}, []);

	useEffect(() => {
		if (!mainSwiperRef.current) return;

		const swiper = mainSwiperRef.current;

		const rafId = requestAnimationFrame(() => {
			if (!safeUpdate(swiper)) return;

			syncNavState(swiper);
			measureNav();
		});

		return () => cancelAnimationFrame(rafId);
	}, [
		slides?.length,
		slidesPerView,
		spaceBetween,
		stable_breakpoints,
		loop,
		syncNavState,
		measureNav
	]);

	useIsomorphicLayoutEffect(() => {
		if (!nav_sides) return;
		if (typeof ResizeObserver === "undefined") return;

		let frame = 0;
		const schedule = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(measureNav);
		};

		const observer = new ResizeObserver(schedule);
		const el = hostRef.current?.querySelector(".swiper");
		const rows = el?.querySelector(".swiper-wrapper");

		if (el) observer.observe(el);
		if (rows) observer.observe(rows);

		schedule();

		return () => {
			cancelAnimationFrame(frame);
			observer.disconnect();
		};
	}, [measureNav, nav_sides, slides?.length]);

	const thumb_items =
		thumbnail_custom_array?.length > 0 ? thumbnail_custom_array : slides;

	const main_class_name = useMemo(
		() =>
			`swiper-slider ${shadow_space_class} ${swiper_className || ""} ${className || ""}`,
		[shadow_space_class, swiper_className, className]
	);

	const nav_wrapper_class_name = useMemo(
		() =>
			`swiper-navigation-wrapper ${navPosition === "bottom" ? "nav-bottom" : "nav-sides"} ${hideNavOnMobile ? "hidden! lg:flex!" : "flex"}`,
		[navPosition, hideNavOnMobile]
	);

	const handleThumbsSwiper = useCallback((swiper) => {
		thumbsSwiperRef.current = swiper;
		setThumbsSwiper(swiper);
	}, []);

	const handleThumbClick = useCallback((swiper) => {
		if (swiper.clickedIndex != null) {
			const i = swiper.clickedIndex;
			setActiveThumbIndex(i);
			onSlideChangeRef.current?.(i);
			mainSwiperRef.current?.slideToLoop(i);
		}
	}, []);

	// فقط اسلایدرهای ساده؛ گالری با thumbnail، زوم، افکت و ... همان رندر Swiper را نگه می‌دارند
	const is_simple_slider =
		!thumbnail && !active_effect && !enableZoom && !centeredSlides && !need_free_mode;

	if (!mounted && is_simple_slider) {
		return (
			<ImageSizesProvider value={slide_sizes}>
				<StaticSlider
					slides={prioritized_slides}
					slidesPerView={slidesPerView}
					spaceBetween={spaceBetween}
					breakpoints={stable_breakpoints}
					grid={stable_grid}
					className={main_class_name}
					dir={props.dir ?? direction}
					pagination={!!pagination}
					hidePaginationOnDesktop={hidePaginationOnDesktop}
				/>
			</ImageSizesProvider>
		);
	}

	return (
		<>
			{/** Main Slider */}
			<ImageSizesProvider value={slide_sizes}>
			<div ref={hostRef} className="relative swiper-main-wrapper">
				<Swiper
					style={style}
					loop={loop}
					dir={direction}
					navigation={navigation_options}
					pagination={pagination_options}
					slidesPerView={slidesPerView}
					spaceBetween={spaceBetween}
					breakpoints={stable_breakpoints}
					modules={swiper_modules}
					thumbs={thumbs_options}
					grid={stable_grid}
					onSwiper={handleSwiper}
					onDestroy={handleDestroy}
					onSlideChange={handleSlideChange}
					onReachBeginning={syncNavState}
					onReachEnd={syncNavState}
					onFromEdge={syncNavState}
					zoom={enableZoom}
					autoplay={autoplay}
					grabCursor={grabCursor}
					className={main_class_name}
					effect={effect}
					parallax={parallax_effect}
					speed={speed}
					centeredSlides={centeredSlides}
					keyboard={KEYBOARD_OPTIONS}
					{...options}
					{...props}
				>
					{prioritized_slides?.map((item, index) => (
						<SwiperSlide key={`slider-item-${index}`} className="h-auto!">
							{enableZoom ? (
								<div
									className="swiper-zoom-container"
									onMouseEnter={() => mainSwiperRef.current?.zoom?.in()}
									onMouseLeave={() => mainSwiperRef.current?.zoom?.out()}
								>
									{item}
								</div>
							) : (
								item
							)}
						</SwiperSlide>
					))}

					{/* Custom Navigation */}
					{navigation && (
						<div className={nav_wrapper_class_name}>

							<button
								ref={prevBtnRef}
								type="button"
								className={`custom-swiper-prev ${prev_class} transition-opacity ${loop ? "opacity-100" : "opacity-0 pointer-events-none"}`}
								aria-label="Previous slide"
							>
								<ChevronRight />
							</button>

							<button
								ref={nextBtnRef}
								type="button"
								className={`custom-swiper-next ${next_class} transition-opacity opacity-100`}
								aria-label="Next slide"
							>
								<ChevronLeft />
							</button>

						</div>
					)}

					{/* Custom Pagination */}
					{pagination && (
						<div
							className={`custom-swiper-pagination ${pagination_class} ${hidePaginationOnDesktop ? "lg:hidden!" : ""}`}
						/>
					)}
				</Swiper>
			</div>
			</ImageSizesProvider>

			{/** Thumbnail Slider */}
			{thumbnail && (
				<Swiper
					onSwiper={handleThumbsSwiper}
					spaceBetween={thumbsSpaceBetween}
					slidesPerView={thumbsSlidesPerView}
					freeMode={true}
					watchSlidesProgress={true}
					modules={THUMB_MODULES}
					className={`swiper-slider-thumb ${swiper_thumbs_className || ""}`}
					centeredSlides={thumbs_centered}
					centeredSlidesBounds={thumbs_centered}
					navigation={thumb_navigation}
					breakpoints={stable_thumb_breakpoints}
					draggable={thumb_draggable}
					onClick={handleThumbClick}
				>
					{thumb_items?.map((item, i) => (
						<SwiperSlide
							key={`thumb-${i}`}
							className={
								activeThumbIndex === i
									? "thumbnail-item thumbnail-item-active"
									: "thumbnail-item"
							}
						>
							{item}
						</SwiperSlide>
					))}
				</Swiper>
			)}
		</>
	);
};

export default SwiperComponent;
